import {
  formatMonthDay,
  MONTH_NAMES,
  updateOrganizationSchema,
  type UpdateOrganizationInput,
} from '@church/domain'
import { useOrganizationProfile, useAccess, useUpdateOrganization } from '@church/data'
import { zodResolver } from '@hookform/resolvers/zod'
import { faChurch, faClock, faFloppyDisk, faGraduationCap, faLink, faPen } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ListSection } from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

export const Route = createFileRoute('/_authed/_shell/settings/profile')({
  component: ProfileTab,
})

function ProfileTab() {
  const { data: org, isPending } = useOrganizationProfile()
  const access = useAccess()
  const [open, setOpen] = useState(false)
  if (isPending || !org) return <Spinner />
  const facts = [
    { icon: faChurch, label: 'Church name', value: org.name },
    { icon: faLink, label: 'URL identifier', value: org.slug },
    { icon: faClock, label: 'Timezone', value: org.timezone },
    {
      icon: faGraduationCap,
      label: 'Children move up a grade on',
      value: formatMonthDay(org.schoolYearStarts) ?? org.schoolYearStarts,
    },
  ]

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Church profile"
        icon={faChurch}
        action={
          access.has('org', 'editor', { orgWide: true }) && (
            <IconButton
              icon={faPen}
              label="Edit profile"
              variant="secondary"
              onClick={() => setOpen(true)}
            />
          )
        }
      >
        <Card className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start">
          {org.logo ? (
            <img
              src={org.logo}
              alt=""
              className="size-16 shrink-0 rounded-xl border border-border-subtle object-contain"
            />
          ) : (
            <span className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-content-brand">
              <Icon icon={faChurch} className="size-7" />
            </span>
          )}
          <dl className="grid flex-1 gap-4 sm:grid-cols-2">
            {facts.map((fact) => (
              <div key={fact.label} className="min-w-0">
                <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-subtle">
                  <Icon icon={fact.icon} className="size-3" />
                  {fact.label}
                </dt>
                <dd className="mt-0.5 truncate text-base text-content-default">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </ListSection>

      <ProfilePanel key={org.id} defaults={org} open={open} onClose={() => setOpen(false)} />
    </div>
  )
}

function ProfilePanel({
  defaults,
  open,
  onClose,
}: {
  defaults: {
    name: string
    slug: string
    timezone: string
    logo: string | null
    schoolYearStarts: string
  }
  open: boolean
  onClose: () => void
}) {
  const update = useUpdateOrganization()
  const formId = useId()

  const form = useForm<UpdateOrganizationInput>({
    resolver: zodResolver(updateOrganizationSchema),
    defaultValues: {
      name: defaults.name,
      timezone: defaults.timezone,
      logo: defaults.logo,
      schoolYearStarts: defaults.schoolYearStarts,
    },
  })
  const starts = form.watch('schoolYearStarts') ?? '08-01'
  const setStarts = (month: string, day: string) =>
    form.setValue('schoolYearStarts', `${month}-${day}`, { shouldDirty: true })

  const submit = form.handleSubmit(async (input) => {
    await update.mutateAsync(input)
    form.reset(input)
    onClose()
  })

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (next) return
        form.reset()
        update.reset()
        onClose()
      }}
      title="Church profile"
      description="The URL identifier is fixed: changing it would break invite links and bookmarks."
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={!form.formState.isDirty}
        >
          Save
        </Button>
      }
    >
      <form id={formId} onSubmit={(e) => void submit(e)} className="space-y-5" noValidate>
        <Field label="Church name" error={form.formState.errors.name?.message}>
          <Input {...form.register('name')} />
        </Field>

        <Field label="Timezone" error={form.formState.errors.timezone?.message}>
          <Input placeholder="America/Chicago" {...form.register('timezone')} />
        </Field>

        <Field
          label="Children move up a grade on"
          description="Grades are worked out from the year each child finishes school, so everyone moves up on this day."
          error={form.formState.errors.schoolYearStarts?.message}
        >
          <div className="flex gap-2">
            <Select
              aria-label="Day"
              value={starts.slice(3, 5)}
              onChange={(e) => setStarts(starts.slice(0, 2), e.target.value)}
              className="w-24"
            >
              {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map((day) => (
                <option key={day} value={day}>
                  {Number(day)}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Month"
              value={starts.slice(0, 2)}
              onChange={(e) => setStarts(e.target.value, starts.slice(3, 5))}
              className="flex-1"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={String(i + 1).padStart(2, '0')}>
                  {name}
                </option>
              ))}
            </Select>
          </div>
        </Field>

        <Field label="Logo URL" error={form.formState.errors.logo?.message}>
          <Input
            placeholder="https://…"
            {...form.register('logo', { setValueAs: (v: string) => (v === '' ? null : v) })}
          />
        </Field>

        <ErrorNote error={update.error} />
      </form>
    </SidePanel>
  )
}
